import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import { Field } from '../components/Field';
import { useAuth } from '../store/auth';
import { formatPhone } from '../lib/phone';

/**
 * Changing your own password.
 *
 * Everyone gets this screen, because everyone is first given a password
 * somebody else chose — a student by their teacher, an instructor by the
 * platform administrator.
 */
export function AccountPage() {
  const { user, restore } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ sessionsEnded: number } | null>(null);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;
  const sameAsCurrent = next.length > 0 && next === current;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<{ sessionsEnded: number }>('/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      });
      setDone(data);
      setCurrent('');
      setNext('');
      setConfirm('');
      // Clears the prompt in the sidebar without a reload.
      await restore();
    } catch (err) {
      setError(errorMessage(err, 'Could not change your password'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title={user?.fullName ?? 'Your account'}
        meta={
          <p className="muted">
            <span className="mono">{formatPhone(user?.phone)}</span>
            {user?.candidateNumber && (
              <>
                {' · candidate '}
                <span className="mono">{user.candidateNumber}</span>
              </>
            )}
          </p>
        }
      />

      <div className="stack">
        {user?.mustChangePassword && !done && (
          <Banner tone="error">
            Your password was set by someone else. Anyone who heard it can sign in as you — choose
            your own below.
          </Banner>
        )}

        {done && (
          <Banner tone="success">
            Password changed.{' '}
            {done.sessionsEnded > 0
              ? `Signed out of ${done.sessionsEnded} other ${done.sessionsEnded === 1 ? 'device' : 'devices'}; this one stays signed in.`
              : 'You are still signed in here.'}
          </Banner>
        )}

        <form className="panel stack rise" onSubmit={(e) => void submit(e)} style={{ maxWidth: '32rem' }}>
          <div>
            <h2 className="panel__title">Change your password</h2>
            <p className="muted">
              Your current password is needed even though you are signed in, so that someone who
              finds this screen open cannot lock you out.
            </p>
          </div>

          {error && <Banner tone="error">{error}</Banner>}

          <Field
            label="Current password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <Field
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            error={
              tooShort
                ? 'At least 8 characters.'
                : sameAsCurrent
                  ? 'This is your current password.'
                  : null
            }
            hint="At least 8 characters. A short phrase you will remember beats a short jumble."
          />
          <Field
            label="Repeat the new password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={mismatch ? 'The two do not match.' : null}
          />

          <div className="row">
            <button
              className="btn btn--primary"
              type="submit"
              disabled={busy || !current || !next || mismatch || tooShort || sameAsCurrent}
            >
              {busy ? 'Changing…' : 'Change password'}
            </button>
          </div>

          <p className="field__hint">
            Changing it signs you out everywhere else, so anyone who knew the old one loses access.
          </p>
        </form>
      </div>
    </>
  );
}
